
import { useState, useEffect, useCallback } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { UserRole } from '@/types/auth';

export const useUserRole = () => {
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUserRole = useCallback(async (userId: string) => {
    if (!userId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('Fetching user role for user ID:', userId);
      
      // First try to get role from user metadata (preferred method)
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error('Error fetching user data:', userError);
        throw userError;
      }
      
      if (userData?.user?.user_metadata?.role) {
        console.log('Found role in user metadata:', userData.user.user_metadata.role);
        setUserRole(userData.user.user_metadata.role as UserRole);
        return;
      }
      
      // The profiles table doesn't have a role column, so we need to check other tables
      // or implement a different way to determine the role
      
      // Check if user is a store admin
      const { data: storeAdminData, error: storeAdminError } = await supabase
        .from('store_admins')
        .select('role')
        .eq('user_id', userId)
        .maybeSingle();
        
      if (storeAdminError && storeAdminError.code !== 'PGRST116') { // Code for no rows returned
        console.error('Error checking store admin role:', storeAdminError);
      }
      
      if (storeAdminData?.role) {
        console.log('Found role in store_admins table:', storeAdminData.role);
        setUserRole(storeAdminData.role === 'owner' ? 'store_admin' : storeAdminData.role as UserRole);
        return;
      }
      
      // Default role if not specified
      console.log('No role found, defaulting to customer');
      setUserRole('customer');
      
    } catch (error) {
      console.error('Error in fetchUserRole:', error);
      setError('Failed to load user role');
      // Default to customer role on error for safety
      setUserRole('customer');
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { 
    userRole, 
    setUserRole, 
    fetchUserRole,
    isLoading,
    error
  };
};
